import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import { lazy, Suspense, useState } from 'react';
import {
	isFitness,
	isMembers,
	isProvider,
} from '../../scripts/resourcesHelpers';

const ClinicalNomination = lazy(
	() => import('@ashn/network-search-react/ClinicalNomination')
);
const FitnessNomination = lazy(
	() => import('@ashn/network-search-react/FitnessNomination')
);

const NOMINATION_TYPES = {
	clinical: {
		title: 'Nominate a Provider',
		Component: ClinicalNomination,
		componentProps: {
			applicationName: 'ASHLink',
			nominationPrivacyPolicyUrl: '/privacy',
			// These components default to assuming an ambient urql Provider
			// exists, but this app does not set one up globally, so each
			// nomination component must create its own internal client.
			includeURQLClient: true,
		},
	},
	fitness: {
		title: 'Nominate a Fitness Club',
		Component: FitnessNomination,
		componentProps: {
			includeURQLClient: true,
		},
	},
};

const NOMINATION_DIALOG_TITLE_ID = 'resource-group-nomination-dialog-title';

const ResourceGroupPageFooter = ({ groupPageId }) => {
	const [nominationType, setNominationType] = useState(null);
	const activeNomination = nominationType
		? NOMINATION_TYPES[nominationType]
		: null;
	const ActiveNominationComponent = activeNomination?.Component;

	const handleCloseNomination = () => setNominationType(null);

	return (
		<Box
			sx={{
				px: { xs: 1, md: 2 },
				py: { xs: 1, md: 1.75 },
				mb: { xs: 1.5, md: 2 },
				bgcolor: 'rgba(0, 0, 0, 0.12)',
			}}
		>
			{isMembers(groupPageId) && (
				<Typography
					variant='body2'
					sx={{
						lineHeight: 1.25,
						whiteSpace: 'normal',
						overflowWrap: 'break-word',
					}}
				>
					Do you have a provider/fitness club that you would like to
					see as part of our network? Let us know!{' '}
					<Link
						component='button'
						type='button'
						color='secondary'
						onClick={() => setNominationType('clinical')}
					>
						Nominate a Provider
					</Link>{' '}
					or{' '}
					<Link
						component='button'
						type='button'
						color='secondary'
						onClick={() => setNominationType('fitness')}
					>
						Nominate a Fitness Club
					</Link>{' '}
				</Typography>
			)}
			{isProvider(groupPageId) && (
				<Typography variant='body1'>
					Have a question? Please visit our{' '}
					<Link href='/' color='secondary'>
						FAQ
					</Link>
				</Typography>
			)}
			{/* Fitness does not currently have need for the extra footer,
				but included in case it is necessary in the future */}
			{isFitness(groupPageId) && null}
			<Dialog
				open={!!activeNomination}
				onClose={handleCloseNomination}
				aria-labelledby={NOMINATION_DIALOG_TITLE_ID}
				fullWidth
				maxWidth='sm'
			>
				<DialogTitle
					id={NOMINATION_DIALOG_TITLE_ID}
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
					}}
				>
					{activeNomination?.title}
					<IconButton
						aria-label='Close'
						onClick={handleCloseNomination}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent dividers>
					{ActiveNominationComponent && (
						<Suspense
							fallback={
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'center',
										py: 4,
									}}
								>
									<CircularProgress />
								</Box>
							}
						>
							<ActiveNominationComponent
								{...activeNomination.componentProps}
							/>
						</Suspense>
					)}
				</DialogContent>
			</Dialog>
		</Box>
	);
};

ResourceGroupPageFooter.propTypes = {
	groupPageId: PropTypes.string.isRequired,
};

export default ResourceGroupPageFooter;
